import React, { useCallback, useEffect, useState } from "react";
import { getLeadsV2, mapTypeToColor } from "./utilities";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import { getManagers, getSalesTeamForLocation } from "../Admin/Utilities/api";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import { normalizeDate } from "../../utilities";
import RequestSnackbar from "../../components/Other/RequestSnackbar";
import DescriptionIcon from "@mui/icons-material/Description";
import { selectRoles } from "../../features/roleSlice";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CloseIcon from "@mui/icons-material/Close";
import debounce from "lodash.debounce";
import { LoadingState } from "../../components/UI/States";

// Shared form-control look so every input on this page follows the theme.
const INPUT_CLASS = "app-input border";

const SECTION_LABEL = "app-divider mb-2 border-b pb-1 font-semibold";

// Assigned manager / salesperson pill.
const PERSON_PILL =
  "flex items-center justify-between gap-1 rounded-full border border-amber-500/30 bg-amber-500/15 py-0.5 pl-2.5 pr-1 text-amber-800 dark:text-amber-200";

const REMOVE_BTN =
  "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-red-600 transition-colors hover:bg-red-500/15 dark:text-red-300";

const QuoteInput = ({ item, updateStatus }) => {
  const [quoteNumber, setQuoteNumber] = useState(item.saleData.quoteNumber);

  // Debounce updateStatus so it only calls the server after user stops typing
  const debouncedUpdate = useCallback(
    debounce((id, value) => {
      updateStatus(id, "quote", value, null);
    }, 500), // Delay of 500ms before sending request
    []
  );

  useEffect(() => {
    if (quoteNumber !== item.saleData.quoteNumber) {
      debouncedUpdate(item._id, quoteNumber);
    }
  }, [quoteNumber, item._id, debouncedUpdate, item.saleData.quoteNumber]);

  return (
    <input
      type="text"
      value={quoteNumber}
      aria-label="Quote #"
      className={`h-9 w-28 rounded-lg px-2.5 text-sm tabular-nums ${INPUT_CLASS}`}
      onChange={(e) => setQuoteNumber(e.target.value)}
    />
  );
};

const OrderInput = ({ item, updateStatus }) => {
  const [orderNumber, setOrderNumber] = useState(item.saleData.orderNumber);

  // Debounce updateStatus so it only calls the server after user stops typing
  const debouncedUpdate = useCallback(
    debounce((id, value) => {
      updateStatus(id, "order", value, null);
    }, 500), // Delay of 500ms before sending request
    []
  );

  useEffect(() => {
    if (orderNumber !== item.saleData.orderNumber) {
      debouncedUpdate(item._id, orderNumber);
    }
  }, [orderNumber, item._id, debouncedUpdate, item.saleData.orderNumber]);

  return (
    <input
      type="text"
      value={orderNumber}
      aria-label="Order #"
      className={`h-9 w-28 rounded-lg px-2.5 text-sm tabular-nums ${INPUT_CLASS}`}
      onChange={(e) => setOrderNumber(e.target.value)}
    />
  );
};

const WebleadManager = (props) => {
  const [leads, setLeads] = useState(null);
  const [managers, setManagers] = useState(null);
  const [salesTeam, setSalesTeam] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [cardsPerPage, setCardsPerPage] = useState(10);
  const [noteData, setNoteData] = useState({});
  const [selectedManager, setManager] = useState(null);
  const [selectedSales, setSales] = useState(null);
  const [confirmation, openConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [logs, setLogs] = useState([]);
  const [cardData, setCardData] = useState({
    telephonerepid: 0,
  });

  const [flag, setFlag] = useState(false);

  const user = useSelector(selectUser);
  const roles = useSelector(selectRoles);

  useEffect(() => {
    // Parse the current URL to get the search parameters
    const queryParams = new URLSearchParams(window.location.search);

    // Get the value of the 'sq' parameter
    const sq = queryParams.get("sq");

    // Update the state with the value of 'sq'
    if (sq) {
      setSearchQuery(sq);
    }
  }, []);

  useEffect(() => {
    const fetchData = () => {
      if (props.branch === "All Locations") {
        getLeadsV2(setLeads, props.type, null, "ALL");
      } else {
        getLeadsV2(
          setLeads,
          props.type,
          props.selectedUser === "ALL" ? props.branch : null,
          props.selectedUser === "ALL" ? "ALL" : props.selectedUser
        );
      }
      switch (user.name) {
        case "Ben Chambers":
          getSalesTeamForLocation(setSalesTeam, "Eastern");
          break;
        case "Scott Foley":
          getSalesTeamForLocation(setSalesTeam, "Western");
          break;
        default:
          getSalesTeamForLocation(setSalesTeam, props.branch);
          break;
      }
    };

    // Fetch data initially
    fetchData();
  }, [props.type, props.branch, props.user, props.selectedUser, flag]);

  useEffect(() => {
    setCurrentPage(1);
  }, [props.type, props.branch, props.user, props.selectedUser, flag]);

  useEffect(() => {
    if (!managers) {
      getManagers(setManagers);
    }
  }, []);

  useEffect(() => {
    if (selectedManager) {
      assignManager(selectedManager);
    }
  }, [selectedManager]);

  useEffect(() => {
    if (selectedSales) {
      assignSales(selectedSales);
    }
  }, [selectedSales]);

  const handleSearch = (e) => {
    setCurrentPage(1);
    setSearchQuery(e.target.value);
  };

  const filteredLeads = leads
    ? leads.filter((lead) =>
        Object.values(lead.webleadEntry).some((value) =>
          value.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : [];

  const indexOfLastCard = currentPage * cardsPerPage;
  const indexOfFirstCard = indexOfLastCard - cardsPerPage;
  const currentCards = filteredLeads.slice(indexOfFirstCard, indexOfLastCard);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const insertNewNote = async () => {
    const rawResponse = await fetch("/api/leads/v2/addNote", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(noteData),
    });
  };

  const updateStatus = async (id, type, status, reason) => {
    const data = {
      user: user.name,
      id: id,
      type: type,
      status: status,
      reason: reason,
    };

    const rawResponse = await fetch("/api/leads/v2/updateProgress", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (rawResponse.message === "Web Lead Updated") {
      await setFlag(!flag);
    }
  };

  const restoreLead = async (id) => {
    const data = {
      user: user.name,
      id: id,
    };

    const rawResponse = await fetch("/api/leads/v2/restoreLead", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (rawResponse.message === "Web Lead Restored") {
      await setFlag(!flag);
    }
  };

  const assignManager = async (data) => {
    data.assignedBy = user;

    const rawResponse = await fetch("/api/leads/v2/assignManager", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    const responseData = await rawResponse.json();

    if (responseData.message === "Web Lead Assigned") {
      await setConfirmationMessage(
        `Manager ${data.Name} assigned to Weblead ID: ${data.id}`
      );
      await openConfirmation(true);
      await setFlag(!flag);
    }
  };

  const assignSales = async (data) => {
    data.assignedBy = user;

    const rawResponse = await fetch("/api/leads/v2/assignSales", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    // Parsing the JSON response from the server
    const responseData = await rawResponse.json();

    if (responseData.message === "Web Lead Assigned") {
      await setConfirmationMessage(
        `Sales ${data.Name} assigned to Weblead ID: ${data.id}`
      );
      await openConfirmation(true);
      await setFlag(!flag);
    }

    if (responseData.message === "User already assigned") {
      await setConfirmationMessage(
        `${data.Name} is already assigned to Weblead ID: ${data.id}`
      );
      await openConfirmation(true);
      await setFlag(!flag);
    }
  };

  const removeSales = async (data) => {
    data.assignedBy = user;

    const rawResponse = await fetch("/api/leads/v2/removeSales", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    // Parsing the JSON response from the server
    const responseData = await rawResponse.json();

    if (responseData.message === "Web Lead Assigned Removed") {
      await setConfirmationMessage(
        `Sales ${data.Name} removed from Weblead ID: ${data.id}`
      );
      await openConfirmation(true);
      await setFlag(!flag);
    }

    if (responseData.message === "User Not Assigned!") {
      await setConfirmationMessage(
        `${data.Name} is not assigned to Weblead ID: ${data.id}`
      );
      await openConfirmation(true);
      await setFlag(!flag);
    }
  };

  const removeManager = async (data) => {
    data.assignedBy = user;

    const rawResponse = await fetch("/api/leads/v2/removeManager", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    // Parsing the JSON response from the server
    const responseData = await rawResponse.json();

    if (responseData.message === "Web Lead Assigned Removed") {
      await setConfirmationMessage(
        `Manager ${data.Name} removed from Weblead ID: ${data.id}`
      );
      await openConfirmation(true);
      await setFlag(!flag);
    }

    if (responseData.message === "User Not Assigned!") {
      await setConfirmationMessage(
        `${data.Name} is not assigned to Weblead ID: ${data.id}`
      );
      await openConfirmation(true);
      await setFlag(!flag);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleToggleContact = (id, type) => {
    // Optimistically update local state
    setLeads((prevLeads) =>
      prevLeads.map((lead) =>
        lead._id === id
          ? {
              ...lead,
              leadProgress: {
                ...lead.leadProgress,
                contactStatus: {
                  ...lead.leadProgress.contactStatus,
                  [type]: {
                    ...lead.leadProgress.contactStatus[type],
                    status: !lead.leadProgress.contactStatus[type].status,
                  },
                },
              },
            }
          : lead
      )
    );
  };

  return (
    <div className="flex w-full flex-col items-center justify-center">
      {leads ? (
        <div className="flex w-full flex-col gap-4">
          {/* Search */}
          <input
            type="text"
            placeholder="Search..."
            aria-label="Search leads"
            value={searchQuery}
            onChange={handleSearch}
            className={`h-10 w-full rounded-xl px-3 text-sm ${INPUT_CLASS}`}
          />

          {currentCards?.map((item) => (
            <Card key={item._id} className="w-full">
              <CardHeader
                className="pb-0"
                title={
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Lead Type */}
                    <Tooltip title="Contact Form Type">
                      <div
                        className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${mapTypeToColor(
                          item.webleadEntry.type
                        )}`}
                      >
                        {item.webleadEntry.type}
                      </div>
                    </Tooltip>

                    {/* Hear About */}
                    {item.webleadEntry.hearAbout && (
                      <Tooltip
                        title={
                          item.webleadEntry.hearAbout === "Other"
                            ? item.webleadEntry.hearOther
                            : "Referral Type"
                        }
                      >
                        <div
                          className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${mapTypeToColor(
                            item.webleadEntry.hearAbout
                          )}`}
                        >
                          {item.webleadEntry.hearAbout}
                        </div>
                      </Tooltip>
                    )}

                    {/* Customer Working On */}
                    {item.webleadEntry.customerWorkingOn && (
                      <Tooltip title="Customer Working On">
                        <div
                          className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${mapTypeToColor(
                            item.webleadEntry.customerWorkingOn
                          )}`}
                        >
                          {item.webleadEntry.customerWorkingOn}
                        </div>
                      </Tooltip>
                    )}

                    {/* Date */}
                    <Tooltip title="Date Submitted">
                      <Typography variant="body2" className="app-muted ml-1">
                        {normalizeDate(item.webleadEntry.entryDate)}
                      </Typography>
                    </Tooltip>

                    {/* Sales Assignment Alert */}
                    {item.assignInfo?.salesAssigned &&
                      item.assignInfo.assignedSales.length < 1 && (
                        <Tooltip title="Assign a Salesperson to this lead!">
                          <p className="ml-1 animate-pulse text-sm font-semibold text-red-700 dark:text-red-300">
                            *No Sales Assigned*
                          </p>
                        </Tooltip>
                      )}
                  </div>
                }
                action={
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Logs */}
                    {item.logs.length > 0 ? (
                      <Tooltip title="Logs">
                        <button
                          type="button"
                          aria-label="Logs"
                          className="app-icon-btn"
                          onClick={() => {
                            handleOpenDialog();
                            setLogs(item.logs);
                          }}
                        >
                          <DescriptionIcon />
                        </button>
                      </Tooltip>
                    ) : (
                      <Tooltip title="No Logs">
                        <span>
                          <button
                            type="button"
                            disabled
                            aria-label="No Logs"
                            className="app-icon-btn cursor-not-allowed opacity-40"
                          >
                            <DescriptionIcon />
                          </button>
                        </span>
                      </Tooltip>
                    )}

                    {/* Status Actions */}
                    {props.type !== "Completed" && props.type !== "Deleted" ? (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          onClick={() =>
                            updateStatus(item._id, "completed", 1, null)
                          }
                        >
                          Complete
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() =>
                            updateStatus(
                              item._id,
                              "deleted",
                              1,
                              `System Delete From ${user.name}`
                            )
                          }
                        >
                          Delete
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        onClick={() => restoreLead(item._id)}
                      >
                        Restore
                      </Button>
                    )}
                  </div>
                }
              />

              <CardContent>
                <Grid container spacing={2}>
                  {/* Contact Info */}
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle1" className={SECTION_LABEL}>
                      Contact Info
                    </Typography>
                    <div className="mt-1 flex items-center gap-2 text-sm font-semibold">
                      <PersonIcon fontSize="small" className="app-muted" />
                      <span>{item.webleadEntry.customerName}</span>
                    </div>
                    <Tooltip title="Click to send Email!">
                      <a
                        className="mt-1.5 flex min-w-0 items-center gap-2 break-all text-sm transition-colors hover:text-[color:var(--app-accent)]"
                        href={`mailto:${item.webleadEntry.customerEmail}?subject=${item.webleadEntry.customerWorkingOn} - ${item.webleadEntry.customerName}&bcc=webleadtracking@bigclumber.com`}
                        target="_blank"
                        onClick={() => {
                          if (user.name !== "Alec Evans") {
                            updateStatus(item._id, "email", true, null);
                          }
                        }}
                      >
                        <EmailIcon fontSize="small" className="app-muted shrink-0" />
                        {item.webleadEntry.customerEmail}
                      </a>
                    </Tooltip>
                    <div className="mt-1.5 flex items-center gap-2 text-sm">
                      <PhoneIcon fontSize="small" className="app-muted" />
                      <span>
                        {item.webleadEntry.customerPhone || "None Provided"}
                      </span>
                    </div>
                    <Tooltip title="Click to view on Map!">
                      <a
                        className="mt-1.5 flex items-start gap-2 text-sm transition-colors hover:text-[color:var(--app-accent)]"
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                          item.webleadEntry.customerAddress
                        )}`}
                        target="_blank"
                      >
                        <HomeIcon fontSize="small" className="app-muted shrink-0" />
                        {item.webleadEntry.customerAddress}
                      </a>
                    </Tooltip>
                  </Grid>

                  {/* Looking For */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" className={SECTION_LABEL}>
                      Looking For
                    </Typography>
                    <Typography
                      variant="body1"
                      className="mt-2 whitespace-pre-wrap leading-relaxed"
                    >
                      {item.webleadEntry.customerLookingFor}
                    </Typography>
                  </Grid>
                  {/* Assignment */}
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle1" className={SECTION_LABEL}>
                      Assignment
                    </Typography>

                    {/* Manager Assignment */}
                    <div className="mb-3">
                      <div className="mb-2 flex items-center gap-2">
                        <Typography variant="body2" className="app-muted w-1/4 shrink-0">
                          Manager
                        </Typography>
                        {!item.assignInfo?.managerAssigned &&
                        (roles.roles.includes("Web Lead Manager") ||
                          roles.roles.includes("General Manager")) ? (
                          <select
                            onChange={(e) => {
                              const selectedIndex = e.target.selectedIndex - 1;
                              const selectedManager = managers[selectedIndex];
                              const managerWithId = {
                                ...selectedManager,
                                id: item._id,
                              };
                              setManager(managerWithId);
                            }}
                            aria-label="Select a manager"
                            className={`h-9 w-3/4 rounded-lg px-2 text-sm ${INPUT_CLASS}`}
                          >
                            <option value="">Select a manager</option>
                            {managers &&
                              managers.map((mgr, index) => (
                                <option key={index} value={mgr.UserID}>
                                  {mgr.Name} - {mgr.Branch}
                                </option>
                              ))}
                          </select>
                        ) : (
                          <div className={`w-3/4 ${PERSON_PILL}`}>
                            <p className="truncate text-sm font-medium">
                              {item.assignInfo.assignedManager.Name}
                            </p>
                            {roles.roles.includes("Web Lead Manager") && (
                              <button
                                type="button"
                                aria-label={`Remove ${item.assignInfo.assignedManager.Name}`}
                                className={REMOVE_BTN}
                                onClick={() =>
                                  removeManager({
                                    id: item._id,
                                    UserID:
                                      item.assignInfo.assignedManager.UserID,
                                    Name: item.assignInfo.assignedManager.Name,
                                  })
                                }
                              >
                                <CloseIcon sx={{ fontSize: 14 }} />
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Sales Assignment */}
                      <div className="flex flex-col">
                        <div className="mb-2 flex items-center gap-2">
                          <Typography variant="body2" className="app-muted w-1/4 shrink-0">
                            Sales
                          </Typography>
                          {(roles.roles.includes("Web Lead Manager") ||
                            roles.roles.includes("General Manager")) && (
                            <select
                              onChange={(e) => {
                                const selectedIndex =
                                  e.target.selectedIndex - 1;
                                const selectedSales = salesTeam[selectedIndex];
                                const salesWithID = {
                                  ...selectedSales,
                                  id: item._id,
                                };
                                setSales(salesWithID);
                              }}
                              aria-label="Assign to Sales"
                              className={`h-9 w-3/4 rounded-lg px-2 text-sm ${INPUT_CLASS}`}
                            >
                              <option value="">Assign to Sales</option>
                              {salesTeam &&
                                salesTeam.map((sales, index) => (
                                  <option key={index} value={sales.UserID}>
                                    {sales.Name} - {sales.Branch}
                                  </option>
                                ))}
                            </select>
                          )}
                        </div>

                        {/* Display Assigned Sales */}
                        {item.assignInfo?.salesAssigned && (
                          <div className="flex flex-wrap gap-1.5">
                            {item.assignInfo.assignedSales.map(
                              (user, index) => (
                                <div key={index} className={PERSON_PILL}>
                                  <p className="text-xs font-medium">
                                    {user?.Name}
                                  </p>
                                  {(roles.roles.includes("Web Lead Manager") ||
                                    roles.roles.includes(
                                      "General Manager"
                                    )) && (
                                    <button
                                      type="button"
                                      aria-label={`Remove ${user?.Name || "salesperson"}`}
                                      className={REMOVE_BTN}
                                      onClick={() =>
                                        removeSales({
                                          id: item._id,
                                          UserID: user?.UserID,
                                          Name: user?.Name,
                                        })
                                      }
                                    >
                                      <CloseIcon sx={{ fontSize: 14 }} />
                                    </button>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </Grid>
                </Grid>

                <Divider className="my-4" />

                <Grid container spacing={2}>
                  {/* Notes */}
                  <Grid item xs={12} md={6}>
                    <Accordion disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography className="font-semibold">Notes</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <div className="flex flex-col space-y-2">
                          {/* Notes List */}
                          <div className="max-h-64 overflow-auto">
                            {item.notes && item.notes.length > 0 ? (
                              item.notes.map((data, index) => (
                                <div
                                  key={index}
                                  className="app-surface mb-2 flex w-full overflow-hidden rounded-xl border"
                                >
                                  <div className="app-divider flex w-1/4 flex-col items-center justify-center border-r p-2 text-center">
                                    <p className="text-xs font-semibold">
                                      {normalizeDate(data.note_date)}
                                    </p>
                                    <p className="app-muted text-xs font-medium">
                                      {data.username}
                                    </p>
                                  </div>
                                  <div className="w-3/4 p-2.5">
                                    <p className="whitespace-pre-wrap text-sm">{data.note}</p>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <p className="app-muted text-sm">No Notes Found</p>
                            )}
                          </div>

                          {/* Add Note Input */}
                          <div className="mt-2 flex items-center gap-2">
                            <input
                              className={`h-10 min-w-0 flex-1 rounded-xl px-3 text-sm ${INPUT_CLASS}`}
                              aria-label="Add Note"
                              placeholder="Add Note..."
                              type="text"
                              value={noteData.message}
                              onChange={(e) =>
                                setNoteData({
                                  id: item._id,
                                  user: user.name,
                                  timestamp: new Date(),
                                  message: e.target.value,
                                })
                              }
                            />
                            <Button
                              variant="contained"
                              color="primary"
                              className="whitespace-nowrap"
                              onClick={() => {
                                if (noteData.message.trim() !== "") {
                                  insertNewNote();
                                  setNoteData({ ...noteData, message: "" });
                                  setFlag(!flag);
                                }
                              }}
                            >
                              Add
                            </Button>
                          </div>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>

                  {/* Lead Progress */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" className={SECTION_LABEL}>
                      Lead Progress
                    </Typography>
                    <div className="flex flex-wrap items-end gap-3">
                      {/* Phone Contact */}
                      <Tooltip title="Click to toggle Phone contact status">
                        <Chip
                          label={`Phone: ${
                            item.leadProgress.contactStatus.phoneContact.status
                              ? "Contacted"
                              : "Not Contacted"
                          }`}
                          color={
                            item.leadProgress.contactStatus.phoneContact.status
                              ? "success"
                              : "error"
                          }
                          clickable
                          onClick={() => {
                            handleToggleContact(item._id, "phoneContact");
                            updateStatus(
                              item._id,
                              "phone",
                              !item.leadProgress.contactStatus.phoneContact
                                .status,
                              null
                            );
                          }}
                        />
                      </Tooltip>

                      {/* Email Contact */}
                      <Tooltip title="Click to toggle Email contact status">
                        <Chip
                          label={`Email: ${
                            item.leadProgress.contactStatus.emailContact.status
                              ? "Contacted"
                              : "Not Contacted"
                          }`}
                          color={
                            item.leadProgress.contactStatus.emailContact.status
                              ? "success"
                              : "error"
                          }
                          clickable
                          onClick={() => {
                            handleToggleContact(item._id, "emailContact");
                            updateStatus(
                              item._id,
                              "email",
                              !item.leadProgress.contactStatus.emailContact
                                .status,
                              null
                            );
                          }}
                        />
                      </Tooltip>

                      {/* Quote Input */}
                      <div className="flex flex-col">
                        <label className="app-muted mb-1 text-xs font-semibold">
                          Quote #
                        </label>
                        <QuoteInput
                          key={item._id}
                          item={item}
                          updateStatus={updateStatus}
                        />
                      </div>

                      {/* Order Input */}
                      <div className="flex flex-col">
                        <label className="app-muted mb-1 text-xs font-semibold">
                          Order #
                        </label>
                        <OrderInput
                          key={item._id}
                          item={item}
                          updateStatus={updateStatus}
                        />
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          {/* Pagination */}
          <div className="mt-2 flex flex-wrap gap-1">
            {[
              ...Array(Math.ceil(filteredLeads.length / cardsPerPage)).keys(),
            ].map((pageNumber) => (
              <button
                type="button"
                key={pageNumber}
                onClick={() => handlePageChange(pageNumber + 1)}
                aria-current={currentPage === pageNumber + 1 ? "page" : undefined}
                className={`h-9 min-w-[36px] rounded-full px-2 text-sm font-semibold tabular-nums transition-colors ${
                  currentPage === pageNumber + 1
                    ? "bg-[color:var(--app-accent-solid)] text-white shadow-sm"
                    : "hover:bg-[color:var(--app-hover)]"
                }`}
              >
                {pageNumber + 1}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <LoadingState label="Loading web leads…" />
      )}

      {/* Snackbar */}
      {confirmation && (
        <RequestSnackbar
          open={confirmation}
          setOpen={openConfirmation}
          message={confirmationMessage}
        />
      )}

      {/* Logs Dialog */}
      <Dialog
        maxWidth="sm"
        fullWidth
        open={openDialog}
        onClose={handleCloseDialog}
      >
        <DialogTitle>Logs</DialogTitle>
        <DialogContent dividers>
          <div className="max-h-80 space-y-2 overflow-auto">
            {logs?.map((item, index) => (
              <div
                className="app-surface flex w-full gap-3 rounded-xl border p-3"
                key={index}
              >
                <div className="app-muted w-1/4 shrink-0 text-xs font-semibold">
                  {normalizeDate(item.timestamp)}
                </div>
                <div className="min-w-0 flex-1 text-sm">{item.message}</div>
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleCloseDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default WebleadManager;
